/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_TIMEOUT } from "utils";
import { getDatesWithoutWeekends, hitungtotaljamkerja } from "utils/func";

function RekapLaporanDinasApel() {
  const [loading, setLoading] = useState(false);
  const [dataRekapDinas, setDataRekapDinas] = useState(null);
  const [tanggalAwal, setTanggalAwal] = useState("");
  const [tanggalAkhir, setTanggalAkhir] = useState("");
  const [userId, setUserId] = useState(null);
  const [userDinasId, setUserDinasId] = useState(null);
  const [listLibur, setListLibur] = useState([]);
  const [totalHariKerja, setTotalHariKerja] = useState(0);
  const [kinerjaData, setKinerjaData] = useState([]);
  const [unitKerja, setUnitKerja] = useState("");
  const [totalWorkingHoursIdeal, setTotalWorkingHoursIdeal] = useState("0:00");

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const getLibur = useCallback(() => {
    axios({
      method: "get",
      url: "https://smartservice.rotendaokab.go.id/api/admin/libur",
      timeout: API_TIMEOUT,
      headers: {
        Authorization: `Bearer ${""}`,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setListLibur(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching libur data:", error);
      });
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid_RSS || user.uid);
    setUserDinasId(user.dinas_id);
    setUnitKerja(user.nama_dinas || "");

    // Set default tanggal: tanggal 1 bulan ini sampai hari ini dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

    const firstDayOfMonth = new Date(
      witaDate.getFullYear(),
      witaDate.getMonth(),
      1
    );

    setTanggalAwal(firstDayOfMonth.toISOString().split("T")[0]);
    setTanggalAkhir(witaDate.toISOString().split("T")[0]);

    getLibur();
  }, []);

  // Fetch daftar hadir (sumber sama seperti smartDH/kinerja) untuk jam kerja & persentase capaian
  const fetchKinerjaData = useCallback(async () => {
    if (!unitKerja || !tanggalAwal || !tanggalAkhir) return;
    try {
      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
        {
          tAwal: tanggalAwal,
          tAkhir: tanggalAkhir,
          unitKerja: unitKerja,
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: API_TIMEOUT,
        }
      );
      if (response.data && response.data.success === 1 && response.data.data) {
        const sorted = [...response.data.data].sort((a, b) => {
          const toMin = (s) => {
            if (!s || s === "0:00") return 0;
            const [h, m] = s.split(":").map(Number);
            return (h || 0) * 60 + (m || 0);
          };
          return toMin(b.workingHours) - toMin(a.workingHours);
        });
        setKinerjaData(sorted);
      } else {
        setKinerjaData([]);
      }
    } catch (err) {
      console.error("Error fetching kinerja/daftar hadir:", err);
      setKinerjaData([]);
    }
  }, [unitKerja, tanggalAwal, tanggalAkhir]);

  const fetchRekapDinas = () => {
    if (!userDinasId || !tanggalAwal || !tanggalAkhir) {
      swal("Error", "Data dinas atau tanggal tidak valid", "error");
      return;
    }

    setLoading(true);
    axios
      .get(
        `${API_ESA_OFFICE}apel-harian/dinas/${userDinasId}/rekap?tanggal_mulai=${tanggalAwal}&tanggal_akhir=${tanggalAkhir}`
      )
      .then((res) => {
        setDataRekapDinas(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching rekap dinas:", err);
        setLoading(false);
        swal("Error", "Gagal mengambil data rekap dinas", "error");
      });
  };

  const handleTanggalChange = () => {
    if (tanggalAwal && tanggalAkhir) {
      fetchKinerjaData(); // endpoint SmartDH/daftar hadir
      fetchRekapDinas();   // endpoint data apel rekap
    }
  };

  const handleDetailClick = (pegawai) => {
    // Pegawai bisa datang sebagai objek langsung atau terbungkus di { original }
    const p = pegawai?.uid_RSS ? pegawai : pegawai?.original || {};
    const uid = p?.uid_RSS || "";
    const nama = p?.nama_pegawai || "";

    if (!uid) {
      swal("Error", "Data pengguna tidak lengkap (uid_RSS kosong)", "error");
      return;
    }

    const encodedNama = encodeURIComponent(nama);
    navigate(
      `/pages/apel-harian/perorang-detail/${uid}?tanggal_awal=${tanggalAwal}&tanggal_akhir=${tanggalAkhir}&nama=${encodedNama}`
    );
  };

  // Auto-fetch data ketika tanggal/dinas berubah: panggil endpoint SmartDH dan data apel rekap
  useEffect(() => {
    if (!userDinasId || !tanggalAwal || !tanggalAkhir || loading) return;
    fetchKinerjaData(); // endpoint SmartDH/daftar hadir (jam kerja & capaian)
    fetchRekapDinas();  // endpoint data apel rekap
  }, [tanggalAwal, tanggalAkhir, userDinasId]);

  // Hitung total hari kerja dan total jam kerja ideal (sama seperti smartDH/kinerja)
  useEffect(() => {
    if (tanggalAwal && tanggalAkhir) {
      const datesWithoutWeekends = getDatesWithoutWeekends(
        tanggalAwal,
        tanggalAkhir,
        Array.isArray(listLibur) ? listLibur : []
      );
      setTotalHariKerja(datesWithoutWeekends.length);
      const totalWh = hitungtotaljamkerja(datesWithoutWeekends);
      setTotalWorkingHoursIdeal(totalWh || "0:00");
    }
  }, [listLibur, tanggalAwal, tanggalAkhir]);

  // Persentase capaian jam kerja (sama seperti smartDH/kinerja)
  const totalWorkingMinutes = useMemo(() => {
    const wh = totalWorkingHoursIdeal;
    if (!wh || wh === "0:00") return 0;
    const [h, m] = String(wh).split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  }, [totalWorkingHoursIdeal]);

  const calculatePersentaseJamKerja = (workingHours) => {
    if (!workingHours || workingHours === "0:00" || totalWorkingMinutes === 0)
      return 0;
    const [h, m] = String(workingHours).split(":").map(Number);
    const minutes = (h || 0) * 60 + (m || 0);
    return Math.round((minutes / totalWorkingMinutes) * 10000) / 100;
  };

  const formatWorkingHours = (workingHours) => {
    if (!workingHours || workingHours === "0:00") return "0:00";
    return workingHours;
  };

  const formatMinutesToHours = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}:${String(m).padStart(2, "0")}`;
  };

  // Fungsi untuk export Excel
  const handleExportExcel = () => {
    if (!dataRekapDinas?.data_pegawai?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Nama Pegawai",
      "Jabatan",
      "Pangkat",
      "Jam Kerja",
      "Persentase Capaian Jam Kerja (%)",
      "Apel Pagi - Hadir",
      "Apel Pagi - Total",
      "Apel Pagi - Persentase",
      "Apel Siang - Hadir",
      "Apel Siang - Total",
      "Apel Siang - Persentase",
      "Persentase Rata-rata",
    ];

    let tableRows = "";
    const sortedData = rows;

    sortedData.forEach((pegawai, index) => {
      const jamKerja = formatWorkingHours(pegawai.workingHours);
      const persenJamKerja = calculatePersentaseJamKerja(pegawai.workingHours);
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${pegawai.nama_pegawai || ""}</td>
          <td>${pegawai.nama_jabatan || ""}</td>
          <td>${pegawai.pangkat || ""}</td>
          <td>${jamKerja}</td>
          <td>${persenJamKerja}%</td>
          <td>${pegawai.apel_pagi?.hadir || 0}</td>
          <td>${pegawai.apel_pagi?.total || 0}</td>
          <td>${pegawai.apel_pagi?.persentase || 0}%</td>
          <td>${pegawai.apel_siang?.hadir || 0}</td>
          <td>${pegawai.apel_siang?.total || 0}</td>
          <td>${pegawai.apel_siang?.persentase || 0}%</td>
          <td>${pegawai.persentase_rata_rata || 0}%</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Rekap Apel Dinas - ${tanggalAwal} s/d ${tanggalAkhir}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Rekap Apel Dinas - ${dataRekapDinas.nama_dinas}</h2>
          <h3>Periode: ${new Date(tanggalAwal).toLocaleDateString(
            "id-ID"
          )} s/d ${new Date(tanggalAkhir).toLocaleDateString("id-ID")}</h3>
          <h3>Statistik:</h3>
          <ul>
            <li>Total Pegawai: ${dataRekapDinas.statistik.total_pegawai}</li>
            <li>Total Apel Pagi Hadir: ${
              dataRekapDinas.statistik.total_apel_pagi_hadir
            }</li>
            <li>Total Apel Siang Hadir: ${
              dataRekapDinas.statistik.total_apel_siang_hadir
            }</li>
            <li>Rata-rata Persentase Pagi: ${
              dataRekapDinas.statistik.rata_rata_persentase_pagi
            }%</li>
            <li>Rata-rata Persentase Siang: ${
              dataRekapDinas.statistik.rata_rata_persentase_siang
            }%</li>
            <li>Rata-rata Persentase Keseluruhan: ${
              dataRekapDinas.statistik.rata_rata_persentase_keseluruhan
            }%</li>
            <li>Hari Kerja: ${dataRekapDinas.periode.hari_kerja}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `rekap-apel-dinas-${tanggalAwal}-${tanggalAkhir}.xls`);
  };

  // Kolom untuk tabel data pegawai
  const columns = [
    { Header: "No", accessor: "index", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "15%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "12%" },
    { Header: "Pangkat", accessor: "pangkat", width: "8%" },
    {
      Header: "Waktu Masuk & Apel Pagi",
      accessor: "masuk_apel_pagi",
      width: "14%",
      Cell: ({ row }) => {
        const r = row.original;
        const jamKerja = formatWorkingHours(r.workingHours);
        const persen = calculatePersentaseJamKerja(r.workingHours);
        const apelPagi = r.apel_pagi;
        const hadirPagi = apelPagi?.hadir || 0;
        const persenPagi = Math.round(
          calculateCorrectPercentage(hadirPagi, totalHariKerja)
        );
        return (
          <MDBox>
            <MDTypography variant="caption" color="secondary" fontWeight="bold" display="block">
              Waktu Masuk (Daftar Hadir):
            </MDTypography>
            <MDTypography variant="caption" color="primary" display="block">
              Jam Kerja: {jamKerja || "-"}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Capaian: {persen}%
            </MDTypography>
            <MDTypography variant="caption" color="secondary" fontWeight="bold" display="block" mt={0.5}>
              Apel Pagi:
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Hadir {hadirPagi}/{totalHariKerja || 0} ({persenPagi}%)
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      Header: "Waktu Pulang & Apel Siang",
      accessor: "pulang_apel_siang",
      width: "14%",
      Cell: ({ row }) => {
        const r = row.original;
        const jamKerja = formatWorkingHours(r.workingHours);
        const persen = calculatePersentaseJamKerja(r.workingHours);
        const apelSiang = r.apel_siang;
        const hadirSiang = apelSiang?.hadir || 0;
        const persenSiang = Math.round(
          calculateCorrectPercentage(hadirSiang, totalHariKerja)
        );
        return (
          <MDBox>
            <MDTypography variant="caption" color="secondary" fontWeight="bold" display="block">
              Waktu Pulang (Daftar Hadir):
            </MDTypography>
            <MDTypography variant="caption" color="primary" display="block">
              Jam Kerja: {jamKerja || "-"}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Capaian: {persen}%
            </MDTypography>
            <MDTypography variant="caption" color="secondary" fontWeight="bold" display="block" mt={0.5}>
              Apel Siang:
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Hadir {hadirSiang}/{totalHariKerja || 0} ({persenSiang}%)
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      Header: "Persentase Rata-rata",
      accessor: "persentase_rata_rata",
      width: "10%",
      Cell: ({ value, row }) => {
        const apelPagiPersentase = calculateCorrectPercentage(
          row.original.apel_pagi?.hadir,
          totalHariKerja
        );
        const apelSiangPersentase = calculateCorrectPercentage(
          row.original.apel_siang?.hadir,
          totalHariKerja
        );
        const rataRata = (apelPagiPersentase + apelSiangPersentase) / 2;

        return (
          <Chip
            label={`${Math.round(rataRata)}%`}
            color={
              Math.round(rataRata) >= 80
                ? "success"
                : Math.round(rataRata) >= 60
                ? "warning"
                : "error"
            }
            size="small"
            variant="filled"
          />
        );
      },
    },
    {
      Header: "Aksi",
      accessor: "aksi",
      width: "10%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => handleDetailClick(row.original)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
          &nbsp;Detail
        </MDButton>
      ),
    },
  ];

  // Rows: gabung data apel + daftar hadir (kinerja) per pegawai
  const rows = useMemo(() => {
    if (!dataRekapDinas?.data_pegawai) return [];

    const mappedData = dataRekapDinas.data_pegawai.map((pegawai, index) => {
      const kinerja = Array.isArray(kinerjaData)
        ? kinerjaData.find(
            (k) =>
              String(k.uid) === String(pegawai.uid_RSS) ||
              (k.nama && pegawai.nama_pegawai && k.nama.trim() === pegawai.nama_pegawai.trim())
          )
        : null;
      const workingHours = kinerja?.workingHours || "0:00";

      return {
        id: pegawai.uid_RSS || `pegawai-${index}`,
        index: index + 1,
        uid_RSS: pegawai.uid_RSS,
        nama_pegawai: pegawai.nama_pegawai,
        nama_jabatan: pegawai.nama_jabatan,
        pangkat: pegawai.pangkat,
        level_jabatan: pegawai.level_jabatan,
        apel_pagi: pegawai.apel_pagi,
        apel_siang: pegawai.apel_siang,
        persentase_rata_rata: pegawai.persentase_rata_rata,
        workingHours,
      };
    });

    return mappedData.sort((a, b) => {
      const levelA = a.level_jabatan ?? 999;
      const levelB = b.level_jabatan ?? 999;
      if (levelA !== levelB) return levelA - levelB;
      const persentaseA = parseFloat(a.persentase_rata_rata) || 0;
      const persentaseB = parseFloat(b.persentase_rata_rata) || 0;
      return persentaseB - persentaseA;
    });
  }, [dataRekapDinas?.data_pegawai, kinerjaData]);

  // Hari kerja periode (untuk denominator total apel pagi/siang di mobile)
  const hariKerja = totalHariKerja || dataRekapDinas?.periode?.hari_kerja || 0;

  // Fungsi untuk menghitung persentase berdasarkan hari kerja yang benar
  const calculateCorrectPercentage = (hadir, totalHariKerja) => {
    if (!totalHariKerja || totalHariKerja === 0) return 0;
    return ((hadir || 0) / totalHariKerja) * 100;
  };

  // Data mobile diurutkan berdasarkan total persentase (rata-rata pagi & siang)
  const mobileSortedPegawai = useMemo(() => {
    if (!dataRekapDinas?.data_pegawai) return [];

    return [...dataRekapDinas.data_pegawai].sort((a, b) => {
      const levelA = a.level_jabatan ?? 999;
      const levelB = b.level_jabatan ?? 999;
      if (levelA !== levelB) return levelA - levelB;
      const aPagiPersentase = calculateCorrectPercentage(
        a?.apel_pagi?.hadir,
        hariKerja
      );
      const aSiangPersentase = calculateCorrectPercentage(
        a?.apel_siang?.hadir,
        hariKerja
      );
      const aAvg = (aPagiPersentase + aSiangPersentase) / 2;

      const bPagiPersentase = calculateCorrectPercentage(
        b?.apel_pagi?.hadir,
        hariKerja
      );
      const bSiangPersentase = calculateCorrectPercentage(
        b?.apel_siang?.hadir,
        hariKerja
      );
      const bAvg = (bPagiPersentase + bSiangPersentase) / 2;

      return bAvg - aAvg;
    });
  }, [dataRekapDinas?.data_pegawai, hariKerja]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox mb={3}>
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    gap={2}
                  >
                    <MDTypography variant="h6" gutterBottom>
                      Rekap Apel Harian Dinas
                    </MDTypography>
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={2}
                      width={{ xs: "100%", md: "auto" }}
                    >
                      <MDInput
                        type="date"
                        label="Tanggal Awal"
                        value={tanggalAwal}
                        onChange={(e) => setTanggalAwal(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                      <MDInput
                        type="date"
                        label="Tanggal Akhir"
                        value={tanggalAkhir}
                        onChange={(e) => setTanggalAkhir(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>

                {dataRekapDinas && (
                  <>
                    <MDTypography variant="button" color="text" gutterBottom>
                      {dataRekapDinas.nama_dinas}
                    </MDTypography>
                    <MDTypography variant="button" color="text" gutterBottom>
                      Periode:{" "}
                      {new Date(
                        dataRekapDinas.periode.tanggal_mulai
                      ).toLocaleDateString("id-ID")}{" "}
                      s/d{" "}
                      {new Date(
                        dataRekapDinas.periode.tanggal_akhir
                      ).toLocaleDateString("id-ID")}{" "}
                      ({dataRekapDinas.periode.hari_kerja} hari kerja)
                    </MDTypography>

                    <Grid container spacing={3} mt={2}>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="info">
                            {dataRekapDinas.statistik.total_pegawai}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Pegawai
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="success">
                            {dataRekapDinas.statistik.total_apel_pagi_hadir}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Apel Pagi Hadir
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="primary">
                            {dataRekapDinas.statistik.total_apel_siang_hadir}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Apel Siang Hadir
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="warning">
                            {dataRekapDinas.statistik.rata_rata_persentase_pagi}
                            %
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata Persentase Pagi
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="secondary">
                            {
                              dataRekapDinas.statistik
                                .rata_rata_persentase_siang
                            }
                            %
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata Persentase Siang
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="dark">
                            {
                              dataRekapDinas.statistik
                                .rata_rata_persentase_keseluruhan
                            }
                            %
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata Keseluruhan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Progress Bar */}
                    <MDBox mt={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <MDTypography variant="button" color="text">
                          Rata-rata Persentase Kehadiran
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          color={
                            dataRekapDinas.statistik
                              .rata_rata_persentase_keseluruhan >= 80
                              ? "success"
                              : dataRekapDinas.statistik
                                  .rata_rata_persentase_keseluruhan >= 60
                              ? "warning"
                              : "error"
                          }
                        >
                          {
                            dataRekapDinas.statistik
                              .rata_rata_persentase_keseluruhan
                          }
                          %
                        </MDTypography>
                      </MDBox>
                      <MDProgress
                        variant="determinate"
                        value={
                          dataRekapDinas.statistik
                            .rata_rata_persentase_keseluruhan
                        }
                        color={
                          dataRekapDinas.statistik
                            .rata_rata_persentase_keseluruhan >= 80
                            ? "success"
                            : dataRekapDinas.statistik
                                .rata_rata_persentase_keseluruhan >= 60
                            ? "warning"
                            : "error"
                        }
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {dataRekapDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Pegawai:{" "}
                        {new Date(tanggalAwal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}{" "}
                        s/d{" "}
                        {new Date(tanggalAkhir).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {dataRekapDinas.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        mobileSortedPegawai.map((pegawai, index) => (
                          <Card
                            key={`${pegawai.uid_RSS || "pegawai"}-${index}`}
                          >
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${pegawai.uid_RSS}.jpg`}
                                      alt={pegawai.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {pegawai.nama_pegawai}
                                  </MDTypography>
                                  <MDBox
                                    mt={1}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={0.5}
                                  >
                                    {(() => {
                                      const k = Array.isArray(kinerjaData)
                                        ? kinerjaData.find(
                                            (x) =>
                                              String(x.uid) === String(pegawai.uid_RSS) ||
                                              (x.nama && pegawai.nama_pegawai && x.nama.trim() === pegawai.nama_pegawai.trim())
                                          )
                                        : null;
                                      const wh = k?.workingHours || "0:00";
                                      const persen = calculatePersentaseJamKerja(wh);
                                      return (
                                        <>
                                          <Chip
                                            label={`Jam Kerja: ${formatWorkingHours(wh)}`}
                                            color="primary"
                                            size="small"
                                          />
                                          <Chip
                                            label={`Capaian: ${persen}%`}
                                            color={persen >= 80 ? "success" : persen >= 60 ? "warning" : "error"}
                                            size="small"
                                          />
                                        </>
                                      );
                                    })()}
                                    <Chip
                                      label={`Apel Pagi: ${pegawai.apel_pagi?.hadir || 0}/${hariKerja}`}
                                      color="info"
                                      size="small"
                                    />
                                    <Chip
                                      label={`Apel Siang: ${pegawai.apel_siang?.hadir || 0}/${hariKerja}`}
                                      color="success"
                                      size="small"
                                    />
                                    <Chip
                                      label={`Rata2: ${Math.round(
                                        pegawai?.persentase_rata_rata != null
                                          ? parseFloat(pegawai.persentase_rata_rata)
                                          : (parseFloat(pegawai?.apel_pagi?.persentase || 0) +
                                              parseFloat(pegawai?.apel_siang?.persentase || 0)) /
                                              2
                                      )}%`}
                                      color="warning"
                                      size="small"
                                    />
                                    <Chip
                                      label={`Hari Kerja: ${hariKerja}`}
                                      color="secondary"
                                      size="small"
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Persentase Kehadiran
                                    </MDTypography>
                                    <MDBox mt={0.5}>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Pagi:{" "}
                                        {Math.round(
                                          calculateCorrectPercentage(
                                            pegawai?.apel_pagi?.hadir,
                                            hariKerja
                                          )
                                        )}
                                        %
                                      </MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Siang:{" "}
                                        {Math.round(
                                          calculateCorrectPercentage(
                                            pegawai?.apel_siang?.hadir,
                                            hariKerja
                                          )
                                        )}
                                        %
                                      </MDTypography>
                                    </MDBox>
                                    <MDProgress
                                      value={Math.round(
                                        (calculateCorrectPercentage(
                                          pegawai?.apel_pagi?.hadir,
                                          hariKerja
                                        ) +
                                          calculateCorrectPercentage(
                                            pegawai?.apel_siang?.hadir,
                                            hariKerja
                                          )) /
                                          2
                                      )}
                                      color={
                                        Math.round(
                                          (calculateCorrectPercentage(
                                            pegawai?.apel_pagi?.hadir,
                                            hariKerja
                                          ) +
                                            calculateCorrectPercentage(
                                              pegawai?.apel_siang?.hadir,
                                              hariKerja
                                            )) /
                                            2
                                        ) >= 80
                                          ? "success"
                                          : Math.round(
                                              (calculateCorrectPercentage(
                                                pegawai?.apel_pagi?.hadir,
                                                hariKerja
                                              ) +
                                                calculateCorrectPercentage(
                                                  pegawai?.apel_siang?.hadir,
                                                  hariKerja
                                                )) /
                                                2
                                            ) >= 60
                                          ? "warning"
                                          : "error"
                                      }
                                      label={`${Math.round(
                                        (calculateCorrectPercentage(
                                          pegawai?.apel_pagi?.hadir,
                                          hariKerja
                                        ) +
                                          calculateCorrectPercentage(
                                            pegawai?.apel_siang?.hadir,
                                            hariKerja
                                          )) /
                                          2
                                      )}%`}
                                      sx={{ height: 8, borderRadius: 8 }}
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Jabatan: {pegawai.nama_jabatan}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Dinas: {pegawai.nama_dinas}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Bidang: {pegawai.nama_bidang}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Pangkat: {pegawai.pangkat}
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDButton
                                      variant="gradient"
                                      color="info"
                                      size="small"
                                      onClick={() =>
                                        handleDetailClick({ original: pegawai })
                                      }
                                      sx={{ minWidth: "auto", px: 1 }}
                                    >
                                      <Icon sx={{ fontSize: 16 }}>
                                        visibility
                                      </Icon>
                                      &nbsp;Detail
                                    </MDButton>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={10}
                      showTotalEntries={true}
                      canSearch={true}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data rekap apel dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!dataRekapDinas &&
            !loading &&
            userDinasId &&
            tanggalAwal &&
            tanggalAkhir && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3} textAlign="center">
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      py={4}
                    >
                      <Icon
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      >
                        event_busy
                      </Icon>
                      <MDTypography variant="h6" color="text.secondary">
                        Tidak ada data rekap apel dinas
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        Coba pilih periode tanggal lain atau periksa kembali
                        dinas
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RekapLaporanDinasApel;
